/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';

/**
 * A custom hook that debounces an asynchronous function.
 *
 * This ensures that the provided async function (`fn`) is not called too frequently,
 * reducing unnecessary API requests or expensive computations. It will delay execution
 * until after the specified `delay` has passed since the last invocation.
 *
 * @template T - A function that returns a Promise.
 * @param {T} fn - The async function to debounce.
 * @param {number} delay - The debounce delay in milliseconds.
 * @returns {(...args: Parameters<T>) => Promise<ReturnType<T> | undefined>}
 *   - A debounced version of `fn` that returns a Promise resolving to the function’s return value.
 *
 * @example
 * ```tsx
 * const fetchData = async (query: string) => {
 *   const response = await fetch(`/api/search?q=${query}`);
 *   return response.json();
 * };
 *
 * const debouncedFetchData = useAsyncDebounce(fetchData, 300);
 *
 * const handleSearch = async (query: string) => {
 *   const results = await debouncedFetchData(query);
 *   console.log(results);
 * };
 * ```
 */
const useAsyncDebounce = <
  T extends (...args: any[]) => Promise<ReturnType<T> | any>,
>(
  fn: T,
  delay: number
) => {
  const timeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return async (...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      timeoutRef.current = setTimeout(async () => {
        try {
          const result = await fn(...args);

          resolve(result);
        } catch (error) {
          resolve(undefined);
        }
      }, delay);
    });
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useAsyncDebounce };
